import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useSuccessFailure } from '../context/SuccessFailureContext';
import config from '../config';
import { FiUsers, FiCalendar, FiUser, FiBriefcase, FiCheck } from 'react-icons/fi';
import { Button, Card, cn } from './ui';

const ROLES = [
    { value: 'community_member', label: 'Community Member', desc: 'Discover and follow events', icon: FiUsers },
    { value: 'event_organizer', label: 'Event Organizer', desc: 'Create and manage events', icon: FiCalendar },
    { value: 'event_participant', label: 'Event Participant', desc: 'Attend and buy tickets', icon: FiUser },
    { value: 'service_provider', label: 'Service Provider', desc: 'Offer services & bid for gigs', icon: FiBriefcase },
];

const RegisterStepTwo = ({ userId: propUserId }) => {
    const [role, setRole] = useState('');
    const [errors, setErrors] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from || '/dashboard';
    const { autoLogin } = useAuth();
    const { showSuccessModal } = useSuccessFailure();

    const userId = propUserId || localStorage.getItem('tempUserId');
    if (!userId) {
        return <div className="min-h-screen flex items-center justify-center text-gray-600 dark:text-gray-300">Please go back and complete step 1 first.</div>;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!role) { setErrors({ role: 'Please select a role' }); return; }
        setIsLoading(true);
        try {
            const response = await axios.post(`${config.API_BASE_URL}/users/register-step-two/`,
                { role, user_id: userId }, { withCredentials: true });
            if (response.status === 200 || response.status === 201) {
                try {
                    await autoLogin(response.data.access, response.data.user);
                    showSuccessModal('Registration successful! Welcome to EventSawa!');
                    navigate(from, { replace: true });
                } catch (error) {
                    setErrors({ role: 'Registration successful but login failed. Please log in manually.' });
                }
            }
        } catch (error) {
            setErrors({ role: error.response?.data?.error || 'Failed to complete registration. Please try again.' });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-900 dark:to-gray-800 px-4 py-12">
            <Card className="w-full max-w-2xl">
                <div className="p-8 md:p-12">
                    <div className="text-center mb-8">
                        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Complete your registration</h2>
                        <p className="text-gray-600 dark:text-gray-300">How will you use EventSawa?</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid sm:grid-cols-2 gap-4">
                            {ROLES.map((r) => {
                                const Icon = r.icon;
                                const active = role === r.value;
                                return (
                                    <button
                                        type="button"
                                        key={r.value}
                                        onClick={() => setRole(r.value)}
                                        className={cn(
                                            'text-left p-5 rounded-2xl border-2 transition-all flex items-start gap-4',
                                            active
                                                ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20'
                                                : 'border-gray-200 dark:border-gray-700 hover:border-indigo-400 dark:hover:border-indigo-500'
                                        )}
                                    >
                                        <div className={cn('h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0',
                                            active ? 'bg-indigo-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300')}>
                                            <Icon className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between">
                                                <span className="font-semibold text-gray-900 dark:text-white">{r.label}</span>
                                                {active && <FiCheck className="w-5 h-5 text-indigo-600" />}
                                            </div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{r.desc}</p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        {errors?.role && (
                            <p className="text-sm text-red-600 dark:text-red-400">{errors.role}</p>
                        )}

                        <Button type="submit" size="lg" fullWidth loading={isLoading} disabled={!role}>
                            {isLoading ? 'Processing…' : 'Complete registration'}
                        </Button>
                    </form>
                </div>
            </Card>
        </section>
    );
};

export default RegisterStepTwo;
